import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Zap, Target, TrendingUp, Award, Clock, Brain, Users, Youtube, Gamepad2, Smartphone } from 'lucide-react';
import { Button } from '../components/ui/button';

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      {/* Top Banner */}
      <div className="bg-gradient-to-r from-primary to-secondary text-white py-3 text-center">
        <p className="font-heading text-lg md:text-xl font-bold tracking-wide">
          For Global Desi Parents! 🌍
        </p>
      </div>

      {/* Hero Section - Problem-Focused */}
      <section className="relative px-4 pt-12 pb-20 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-5xl mx-auto">
            {/* Problem Statement */}
            <div className="text-center mb-12">
              <div className="inline-block mb-4 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                <span className="text-primary font-bold text-sm">For Students Grade 1 to Class 5</span>
              </div>
              <h1 data-testid="hero-headline" className="font-heading text-4xl md:text-6xl font-bold tracking-tight mb-6">
                Your Child Isn't Short on Time.
                <span className="block text-destructive mt-2">They're Short on the Right Kind of Time.</span>
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed max-w-3xl mx-auto">
                Between school, tuition, and play, hours disappear into <span className="inline-flex items-center gap-1"><Youtube className="h-5 w-5 inline text-destructive" />YouTube</span>, <span className="inline-flex items-center gap-1"><Gamepad2 className="h-5 w-5 inline text-destructive" />video games</span>, and <span className="inline-flex items-center gap-1"><Smartphone className="h-5 w-5 inline text-destructive" />endless scrolling</span>. <span className="font-semibold text-foreground">What if you could turn that free time into focused, structured learning—on your schedule?</span>
              </p>
            </div>

            {/* Solution Highlight */}
            <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-3xl p-8 md:p-12 border-2 border-primary/20 mb-8">
              <div className="text-center">
                <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                  <span className="text-primary">LittleSteps:</span> Test-Based Learning That Actually Works
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  No more nagging. No daily planning. You set the schedule—daily, alternate days, weekly—and we handle the rest with automated tests that build discipline and track progress.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button data-testid="get-started-btn" onClick={() => navigate('/auth')} size="lg" className="rounded-full px-8 py-6 text-lg font-bold shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1">
                    Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <Button variant="outline" size="lg" className="rounded-full px-8 py-6 text-lg font-bold" onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}>
                    See How It Works
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground mt-6">No credit card required • Cancel anytime after 3 tests</p>
              </div>
            </div>

            {/* Quick Problem Points */}
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="p-6">
                <div className="text-4xl mb-3">⏰</div>
                <p className="font-semibold mb-2">Screen Time > Study Time</p>
                <p className="text-sm text-muted-foreground">Hours lost to passive entertainment</p>
              </div>
              <div className="p-6">
                <div className="text-4xl mb-3">📚</div>
                <p className="font-semibold mb-2">"Go Study" Doesn't Work</p>
                <p className="text-sm text-muted-foreground">Without structure, kids drift</p>
              </div>
              <div className="p-6">
                <div className="text-4xl mb-3">😓</div>
                <p className="font-semibold mb-2">Parents Lack Time</p>
                <p className="text-sm text-muted-foreground">Can't plan & track daily</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-foreground text-background py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div><div className="text-4xl font-bold mb-2">5</div><div className="text-sm opacity-80">Core Subjects</div></div>
            <div><div className="text-4xl font-bold mb-2">Flexible</div><div className="text-sm opacity-80">Your Schedule</div></div>
            <div><div className="text-4xl font-bold mb-2">7 Days</div><div className="text-sm opacity-80">Weekly Reports</div></div>
            <div><div className="text-4xl font-bold mb-2">100%</div><div className="text-sm opacity-80">Gamified Learning</div></div>
          </div>
        </div>
      </section>

      {/* Problem → Solution */}
      <section className="px-4 py-20 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6 text-destructive">The Problem</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 rounded-xl bg-destructive/5 border border-destructive/10">
                  <div className="w-8 h-8 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0 mt-1"><span className="text-destructive font-bold">1</span></div>
                  <p className="text-muted-foreground">Hours disappear into YouTube, games, and scrolling</p>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-xl bg-destructive/5 border border-destructive/10">
                  <div className="w-8 h-8 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0 mt-1"><span className="text-destructive font-bold">2</span></div>
                  <p className="text-muted-foreground">"Go study" doesn't work without structure</p>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-xl bg-destructive/5 border border-destructive/10">
                  <div className="w-8 h-8 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0 mt-1"><span className="text-destructive font-bold">3</span></div>
                  <p className="text-muted-foreground">Parents lack time to plan and track progress</p>
                </div>
              </div>
            </div>
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6 text-secondary">The Solution</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 rounded-xl bg-secondary/5 border border-secondary/10">
                  <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center flex-shrink-0 mt-1"><Target className="h-4 w-4 text-white" /></div>
                  <p className="text-muted-foreground">Scheduled tests turn free time into focused learning</p>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-xl bg-secondary/5 border border-secondary/10">
                  <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center flex-shrink-0 mt-1"><Zap className="h-4 w-4 text-white" /></div>
                  <p className="text-muted-foreground">Gamification makes learning feel like achievement</p>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-xl bg-secondary/5 border border-secondary/10">
                  <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center flex-shrink-0 mt-1"><TrendingUp className="h-4 w-4 text-white" /></div>
                  <p className="text-muted-foreground">Weekly reports show progress without daily nagging</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="px-4 py-20 md:px-8 lg:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">How LittleSteps Works</h2>
            <p className="text-xl text-muted-foreground">Set it once. Track weekly. See results.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: Users, title: 'Set Goals', desc: 'Choose subjects, grade level, schedule' },
              { icon: Brain, title: 'We Plan', desc: 'Custom test sequences designed for growth' },
              { icon: Clock, title: 'Child Takes Tests', desc: '15-min focused challenges' },
              { icon: Award, title: 'Track Progress', desc: 'Weekly reports + gamification' }
            ].map((step, idx) => (
              <div key={idx} className="text-center">
                <div className="w-20 h-20 rounded-3xl bg-primary mx-auto mb-4 flex items-center justify-center shadow-lg">
                  <step.icon className="h-10 w-10 text-primary-foreground" />
                </div>
                <h3 className="font-bold text-xl mb-2">{step.title}</h3>
                <p className="text-muted-foreground text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subjects */}
      <section className="px-4 py-20 md:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold mb-12">All Essential Subjects Covered</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {['English', 'Mathematics', 'Science', 'Phonics & Early Literacy', 'Reasoning & Logical Thinking'].map((subject, idx) => (
              <div key={idx} className="card-base p-6 text-center hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-full bg-primary/10 mx-auto mb-3 flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary">{subject[0]}</span>
                </div>
                <p className="font-semibold">{subject}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 py-20 md:px-8 lg:px-12 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">Start Building Better Learning Habits Today</h2>
          <p className="text-xl mb-8 opacity-90">1-month minimum. Cancel after 3 tests. No risk.</p>
          <Button data-testid="cta-start-btn" onClick={() => navigate('/auth')} size="lg" className="bg-white text-primary hover:bg-white/90 rounded-full px-8 py-6 text-lg font-bold shadow-xl">
            Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;